import { useStyle } from "./style";
import broadbandPlan from "@static/img/broadband/broadbandOfferLarge.png";
import broadbandPlanMobile from "@static/img/broadband/broadbandOfferLargeCrop.png";
import { Box, Grid, Stack, SvgIcon, Typography } from "@mui/material";
import { Prequal } from "@features/prequal/Prequal";
import { OfferPrice } from "@common/OfferPrice/OfferPrice";
import { EServiceGroup, ROUTES } from "@constants";
import { useHomeBroadband } from "@features/home/broadband/useHomeBroadband";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { WarningBanner } from "@common/WarningBanner/WarningBanner";
import WarningIcon from "@mui/icons-material/Warning";
import CheckIcon from "@static/icons/CheckRounded.svg";
import HeartIcon from "@static/icons/Heart.svg";
import RocketIcon from "@static/icons/Rocket.svg";
import PaperPlaneIcon from "@static/icons/PaperPlane.svg";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useDeviceType } from "@hooks/useDeviceType";
import { HomeBroadbandWhyBlock } from "@features/home/broadband/HomeBroadbandWhyBlock";
import { MarketingInformationCardsWifiBoost } from "@features/home/broadband/MarketingInformationCardsWifiBoost";
import { HomeMarketingInformationBanner } from "@features/home/common/HomeMarketingInformationBanner/HomeMarketingInformationBanner";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { HomeBroadbandFrequentlyAskedQuestions } from "./HomeBroadbandFrequentlyAskedQuestions/HomeBroadbandFrequentlyAskedQuestions";
import { HomeStickyBanner } from "../common/HomeStickyBanner/HomeStickyBanner";
import { BUTTON_IDS } from "@features/dataLayer/buttonIds";
import LockIcon from "@static/icons/Lock.svg";
import { CardTag } from "@common/CardTag/CardTag";
import { useTheme } from "@mui/material/styles";
import { Helmet } from "react-helmet";
import { getOfferPrice } from "@utils/offerUtils";

export const HomeBroadband = () => {
    const { isDesktop, isSmallMobile, isMobile, isMobileOrTablet, isTabletOrDesktop } = useDeviceType();
    const { classes } = useStyle({ isDesktop, isSmallMobile, isMobile, isMobileOrTablet, isTabletOrDesktop });
    const theme = useTheme();
    const {
        broadbandOffer,
        error,
        isLoading,
        retry,
        setPrequal,
        allowAddingBroadbandOffer,
        eirCodeFieldRef,
        handleBannerButtonClick,
    } = useHomeBroadband();
    const { t } = useTranslation("home");
    const { push } = useHistory();

    const renderPrequal = () => {
        if (allowAddingBroadbandOffer) {
            return <Prequal eirCodeFieldRef={eirCodeFieldRef} isWrapped={isMobileOrTablet} setPrequal={setPrequal} />;
        }

        return (
            <Stack spacing={2}>
                <WarningBanner content={t("tooMuchBroadband")} icon={<WarningIcon />} />
                <div>
                    <LabeledButton color="primary" labelText={t("goCart")} onClick={() => push(ROUTES.CART_PAGE_URL)}>
                        {t("goCart")}
                    </LabeledButton>
                </div>
            </Stack>
        );
    };

    const renderHomeStickyBanner = () =>
        broadbandOffer && (
            <HomeStickyBanner
                buttonId={BUTTON_IDS.BROADBAND_FLOW.STICKY.CHECK_AVAILABILITY}
                buttonLabel={t("checkAvailability")}
                color="tertiary"
                title={broadbandOffer.description}
                onClick={handleBannerButtonClick}
            />
        );

    return (
        <HandleErrorAndLoading error={error} isHomePage isLoading={isLoading} retryFetch={retry}>
            <Helmet>
                <link href="https://shop.gomo.ie/broadband" rel="canonical" />
                <meta content={t("broadbandPageDescription")} name="description" />
                <meta content={t("broadbandPageDescription")} property="og:description" />
                <meta content={t("broadbandPageTitle")} property="og:title" />
                <meta content="width=device-width, initial-scale=1.0" name="viewport" />
                <meta content="index, follow" name="robots" />
                <title>{t("broadbandPageTitle")}</title>
            </Helmet>
            {broadbandOffer && (
                <>
                    {isTabletOrDesktop && renderHomeStickyBanner()}
                    <div className={classes.container}>
                        <Grid className={classes.prequal} container gap={isMobile ? 4 : 0}>
                            <Grid className={classes.prequalForm} item md={6} xs={12}>
                                <div className={classes.header}>
                                    <div>
                                        <Box width={isMobile ? "auto" : "fit-content"}>
                                            <CardTag
                                                backgroundColor={theme.palette.quaternary.main}
                                                color="secondary"
                                                isCompactLayout
                                                label={t("newExistingGoMoMobileCustomers")}
                                            />
                                        </Box>
                                        <Typography variant="h1">{broadbandOffer.description}</Typography>
                                    </div>
                                    <Typography variant="large">{broadbandOffer.comment}</Typography>
                                </div>
                                <div className={classes.offerPrice}>
                                    <OfferPrice price={getOfferPrice(broadbandOffer)} priceColor="#33A1A9" />
                                </div>
                                <div className={classes.usages}>
                                    <SvgIcon
                                        className={classes.iconPrimary}
                                        color="primary"
                                        component={LockIcon}
                                        inheritViewBox
                                    />
                                    <Typography className={classes.firstUsageDescription}>
                                        {broadbandOffer.usages[0]?.description}
                                    </Typography>
                                </div>
                                {renderPrequal()}
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <img
                                    alt="Broadband plan"
                                    className={classes.imageGum}
                                    src={isMobile ? broadbandPlanMobile : broadbandPlan}
                                />
                            </Grid>
                        </Grid>
                        <div className={classes.containerDark}>
                            <div className={classes.marketing}>
                                <Typography align="center" variant="h2">
                                    {t("broadbandWhy.title")}
                                </Typography>
                                <div className={classes.inlineContainer}>
                                    <HomeBroadbandWhyBlock
                                        description={t("broadbandWhy.priceForLife.description")}
                                        isCompactLayout={isMobileOrTablet}
                                        svgIcon={CheckIcon}
                                        title={t("broadbandWhy.priceForLife.title")}
                                    />
                                    <HomeBroadbandWhyBlock
                                        description={t("broadbandWhy.experienceFreedom.description")}
                                        isCompactLayout={isMobileOrTablet}
                                        svgIcon={PaperPlaneIcon}
                                        title={t("broadbandWhy.experienceFreedom.title")}
                                    />
                                    <HomeBroadbandWhyBlock
                                        description={t("broadbandWhy.reliableNetwork.description")}
                                        isCompactLayout={isMobileOrTablet}
                                        svgIcon={RocketIcon}
                                        title={t("broadbandWhy.reliableNetwork.title")}
                                    />
                                    <HomeBroadbandWhyBlock
                                        description={t("broadbandWhy.goMoFamily.description")}
                                        isCompactLayout={isMobileOrTablet}
                                        svgIcon={HeartIcon}
                                        title={t("broadbandWhy.goMoFamily.title")}
                                    />
                                </div>
                            </div>
                        </div>
                        <HomeMarketingInformationBanner
                            description={t("broadbandMarketing.description")}
                            title={t("broadbandMarketing.title")}
                            variant={EServiceGroup.SA_BB}
                        />
                        <MarketingInformationCardsWifiBoost />
                        <HomeBroadbandFrequentlyAskedQuestions />
                        {!isTabletOrDesktop && renderHomeStickyBanner()}
                    </div>
                </>
            )}
        </HandleErrorAndLoading>
    );
};
